import React, {
	Component,
	type ComponentType,
	cloneElement,
	type ReactNode,
	type ReactElement,
} from 'react';
import ReactDOM from 'react-dom';
// Remove ThemeProvider on compiled.migration.jsw.tanuki FF cleanup
// eslint-disable-next-line jira/restricted/styled-components, jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { ThemeProvider } from 'styled-components';

import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import {
	Droppable,
	type DroppableProvided,
	type DraggableChildrenFn,
	type DroppableStateSnapshot,
} from 'react-beautiful-dnd';

import {
	type ColumnId,
	type SwimlaneId,
	ColumnTheme,
	columnThemes,
	TransitionZoneIndicatorContainerContext,
	getColumnDescriptor,
	CARD_DND_TYPE,
} from '@atlassian/jira-platform-board-kit';
import type { ColumnThemeProps } from '@atlassian/jira-platform-board-kit/src/common/types';
import { ColumnWrapper } from '@atlassian/jira-platform-board-kit/src/common/ui/column';
import { Overlay } from '@atlassian/jira-platform-board-kit/src/common/ui/overlay';
import {
	Outline,
	CardDroppableTarget,
} from '@atlassian/jira-platform-board-kit/src/ui/column/draggable-column/column';

const cardListCssClass = '__board-test-hook__column';

type ColumnRenderFunction<T> = (
	dropProvided: DroppableProvided,
	snapshot: DroppableStateSnapshot,
	props: T,
) => ReactElement;

type Props = {
	isDragging: boolean;
	isDraggable: boolean;
	isUpdating: boolean;
	isOutlined?: boolean;
	isDropDisabled?: boolean;
	isCollapsed?: boolean;
	isFlexible?: boolean;
	id: ColumnId;
	header?: ReactElement | null | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	akDragHandleProps: any;
	appearance: ColumnTheme;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	children?: ColumnRenderFunction<any> | ReactNode | null | undefined;
	swimlaneId?: SwimlaneId;
	footer: ReactNode;
	dropZoneMode?: 'standard' | 'virtual';
	childrenDropZoneType: string;
	TransitionZones?: ComponentType<{
		columnId: ColumnId;
		swimlaneId?: SwimlaneId;
	}>;
	renderClone?: DraggableChildrenFn;
};

// eslint-disable-next-line jira/react/no-class-components
export default class Column extends Component<Props> {
	static defaultProps = {
		isDragging: false,
		isDropDisabled: false,
		isUpdating: false,
		isOutlined: false,
		isCollapsed: false,
		appearance: 'default',
		akDragHandleProps: {},
		footer: null,
		childrenDropZoneType: CARD_DND_TYPE,
		dropZoneMode: 'standard',
		renderClone: undefined, // Yes, this must be undefined. If we noop/null here, the DnD will break
	};

	static contextType = TransitionZoneIndicatorContainerContext;

	context!: React.ContextType<typeof TransitionZoneIndicatorContainerContext>;

	getTheme = (theme?: ColumnTheme | null) => {
		if (theme && columnThemes[theme]) {
			return columnThemes[theme];
		}

		return columnThemes.default;
	};

	renderPortalTransitionZone() {
		const { id, swimlaneId, TransitionZones } = this.props;
		if (isNil(TransitionZones)) return null;

		if (this.context) {
			const ref = this.context.getRef(id);
			if (ref && ref.current) {
				return ReactDOM.createPortal(
					<TransitionZones columnId={id} swimlaneId={swimlaneId} />,
					ref.current,
				);
			}
		}
		return <TransitionZones columnId={id} swimlaneId={swimlaneId} />;
	}

	renderAkColumn(theme: ColumnThemeProps) {
		const {
			id,
			isDragging,
			isDraggable,
			isDropDisabled,
			isOutlined,
			isUpdating,
			header,
			swimlaneId,
			akDragHandleProps,
			appearance,
			childrenDropZoneType,
			dropZoneMode,
			TransitionZones,
			renderClone,
			isCollapsed,
			isFlexible,
		} = this.props;

		return (
			<ThemeProvider theme={theme}>
				<StyledColumnWrapper
					// eslint-disable-next-line jira/react/no-classname-attribute, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className={cardListCssClass}
					isCollapsed={isCollapsed}
					isFlexible={isFlexible}
					appearance={appearance ?? ColumnTheme.Default}
				>
					{header &&
						cloneElement(header, {
							isDragging,
							isDraggable,
							isUpdating,
							akDragHandleProps,
							appearance,
						})}
					{TransitionZones && this.renderPortalTransitionZone()}
					<Droppable
						droppableId={getColumnDescriptor(id, swimlaneId)}
						type={childrenDropZoneType}
						mode={dropZoneMode}
						isDropDisabled={isDropDisabled}
						renderClone={renderClone}
					>
						{(dropProvided, snapshot) => {
							// If you want to use render props this component will give you back the props and some droppable
							// context for the external component to consume
							// Example: src/packages/software/board/board-container/board/columns-container/column/view.js
							if (!__SERVER__ && isFunction(this.props.children)) {
								// Our v-list under this column will need visibility over droppableProps
								const renderFunction: ColumnRenderFunction<Props> =
									this.props.children || (() => <div />);

								return renderFunction(dropProvided, snapshot, this.props);
							}

							// FIXME: This needs to be checked - done as part of typescript upgrade.
							// It can be more than ReactNode and typescript is not happy about this!
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							const children = this.props.children as ReactNode;
							// Fallback to generic non-virtualized implementation
							return (
								<CardDroppableTarget
									innerRef={dropProvided.innerRef}
									{...dropProvided.droppableProps}
									isDisabled={isDropDisabled}
									// eslint-disable-next-line jira/react/no-classname-attribute, @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
									className="__board-test-hook__card-list-container"
								>
									{children}
									{dropProvided.placeholder}
									{this.props.footer}
								</CardDroppableTarget>
							);
						}}
					</Droppable>
					{isOutlined && <Outline withHeader={header} />}
					{isUpdating && <Overlay />}
				</StyledColumnWrapper>
			</ThemeProvider>
		);
	}

	render() {
		const columnTheme = this.getTheme(this.props.appearance);
		return this.renderAkColumn(columnTheme);
	}
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledColumnWrapper = styled(ColumnWrapper)({
	display: 'flex',
	flexDirection: 'column',
});
